import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchCoursesAction,
  fetchCourseMetadataAction,
} from "@/modules/learningCenter/features/CourseManagement/slices/courseListSlice";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export interface CourseListFilters {
  search: string;
  status: string;
}

export const useCourseList = () => {
  const dispatch = useAppDispatch();

  const courses = useAppSelector(
    (state: RootState) => state.courseList.courses,
  );
  const totalCount = useAppSelector(
    (state: RootState) => state.courseList.totalCount,
  );
  const loading = useAppSelector(
    (state: RootState) => state.courseList.fetchLoading,
  );
  const error = useAppSelector(
    (state: RootState) => state.courseList.fetchError,
  );
  const metadata = useAppSelector(
    (state: RootState) => state.courseList.metadata,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "title", desc: false },
  ]);
  const [filters, setFilters] = useState<CourseListFilters>({
    search: "",
    status: "",
  });

  useEffect(() => {
    dispatch(fetchCourseMetadataAction());
  }, [dispatch]);

  useEffect(() => {
    const sortBy = sorting.length ? sorting[0].id : undefined;
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "desc"
        : "asc"
      : undefined;

    dispatch(
      fetchCoursesAction({
        search: filters.search || undefined,
        status: filters.status || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const refetch = () => {
    const sortBy = sorting.length ? sorting[0].id : undefined;
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "desc"
        : "asc"
      : undefined;
    dispatch(
      fetchCoursesAction({
        search: filters.search || undefined,
        status: filters.status || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
      }),
    );
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (
    filterKey: keyof CourseListFilters,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return {
    courses,
    totalCount,
    loading,
    error,
    metadata,
    pagination,
    sorting,
    filters,
    refetch,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
};
